import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Group, Loader, Stack, Tabs, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { notifications } from '@mantine/notifications';
import { Breadcrumbs, EditableDataTable, type EditableDataTableColumn } from '@components';
import {
  type PickupPointProductStockRow,
  usePickupPoint,
  usePickupPoints,
  usePickupPointProductStocks,
  useUpdatePickupPoint,
  useAddException,
  useUpdateException,
  useDeleteException,
  useDeletePickupPoint,
} from '@modules/pickup-points';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@modules/products';
import type { ProductPickupStockFormRow } from '@modules/products/types';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import {
  isSellerOwner,
  sellerCanAccessPickupPointId,
  sellerHasSection,
} from '@modules/seller-staff';
import {
  Alert,
  BackButton,
  Button,
  CachedImage,
  Checkbox,
  ControlledInput,
  DoubleCheckModal,
  IconTextAction,
  Input,
  Modal,
  QuietAddTrigger,
  QuietIconButton,
  Radio,
  RadioGroup,
  RefetchButton,
  Select,
  Switch,
  Tooltip,
} from '@shared/ui';
import { useIsMobile } from '@shared/hooks';
import ScheduleTimeInput from './ScheduleTimeInput';
import type { PickupPointDeliveryProvider, SellerDeliveryZone, WorkingHours } from '@modules/pickup-points/types';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import locationFlowerIconUrl from '../../assets/location-flower.png';
import {
  IoArrowUndoOutline,
  IoCalendarOutline,
  IoCartOutline,
  IoCheckmark,
  IoClose,
  IoCloseOutline,
  IoCreateOutline,
  // IoHourglassOutline, // было для minHoursBefore — теперь env
  IoSaveOutline,
  IoTimeOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import 'leaflet/dist/leaflet.css';
import '@shared/utils/leaflet-default-icon';
import '@shared/styles/page.css';
import './PickupPointDetailsPage.css';
import { formatPickupPhoneForInput } from '@modules/pickup-points/utils/formatPickupPhone';
import PickupPointMetroFields, {
  applyNearestMetroToForm,
} from '@modules/pickup-points/components/PickupPointMetroFields';
import { hasMetroCatalog } from '@modules/pickup-points/data/metroStations';
import {
  pickupPointDetailsContactSchema,
  type PickupPointDetailsContactFormValues,
} from '@modules/pickup-points/utils/pickupPointDetailsContactValidation';
import { getPickupPointMutationErrorMessage } from '@modules/pickup-points/utils/pickupPointMutationError';

const DAYS_OF_WEEK = [
  { key: '1', label: 'Понедельник', shortLabel: 'Пн' },
  { key: '2', label: 'Вторник', shortLabel: 'Вт' },
  { key: '3', label: 'Среда', shortLabel: 'Ср' },
  { key: '4', label: 'Четверг', shortLabel: 'Чт' },
  { key: '5', label: 'Пятница', shortLabel: 'Пт' },
  { key: '6', label: 'Суббота', shortLabel: 'Сб' },
  { key: '7', label: 'Воскресенье', shortLabel: 'Вс' },
];

const pickupPointMapIcon = new L.Icon({
  iconUrl: locationFlowerIconUrl,
  iconRetinaUrl: locationFlowerIconUrl,
  iconSize: [26, 32],
  iconAnchor: [13, 32],
  popupAnchor: [0, -28],
});

const ZONE_COLOR_OPTIONS = [
  { value: 'blue', label: 'Лавандовый', hex: '#8B7CF6' },
  { value: 'red', label: 'Розовый', hex: '#E65A8C' },
  { value: 'green', label: 'Мятный', hex: '#2DD4BF' },
  { value: 'yellow', label: 'Солнечный', hex: '#F5C542' },
  { value: 'orange', label: 'Персиковый', hex: '#FB923C' },
] as const;
const ZONE_COLOR_ORDER = ZONE_COLOR_OPTIONS.map((c) => c.value);

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { value: 'Europe/Samara', label: 'Самара (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург / Пермь (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Якутск (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Магадан (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)' },
];

const SLOT_SETTING_HINTS: Record<string, { icon: ReactNode; hint: string }> = {
  slotDuration: {
    icon: <IoTimeOutline size={18} />,
    hint: 'На сколько минут делится рабочий день. 60 — покупатель выбирает часовые окна (10:00–11:00), 120 — окна по два часа.',
  },
  maxOrdersPerSlot: {
    icon: <IoCartOutline size={18} />,
    hint: 'Сколько заказов вы успеваете собрать за одно окно. Когда лимит заполнен, это время больше не показывается покупателям.',
  },
  advanceDays: {
    icon: <IoCalendarOutline size={18} />,
    hint: 'На сколько дней вперёд покупатель может выбрать время.',
  },
  // Глобально через env MIN_HOURS_BEFORE_BOOKING (не настройка точки)
  // minHoursBefore: {
  //   icon: <IoHourglassOutline size={18} />,
  //   hint: 'За сколько часов до начала окна ещё можно оформить заказ.',
  // },
};

function SlotSettingName({ id, name }: { id: string; name: string }) {
  const help = SLOT_SETTING_HINTS[id];
  return (
    <div className="pickup-point-slot-setting">
      {help ? (
        <span className="pickup-point-slot-setting__icon" aria-hidden>
          {help.icon}
        </span>
      ) : null}
      <div className="pickup-point-slot-setting__body">
        <div className="pickup-point-slot-setting__name">{name}</div>
        {help ? (
          <Text size="xs" c="dimmed" className="pickup-point-slot-setting__hint">
            {help.hint}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

const DELIVERY_OPTION_HINTS = {
  MARKETPLACE:
    'Курьеры маркетплейса везут заказ покупателю. Можно компенсировать часть стоимости доставки — покупатель увидит меньшую сумму.',
  SELLER:
    'Вы сами доставляете заказы: нарисуйте зоны на карте и задайте цену для каждой.',
  COMPENSATION:
    'Часть стоимости доставки оплачиваете вы — покупатель видит скидку. Указанный процент удерживается с вашего баланса при заказе.',
} as const;

function usePickupStockPersist(
  row: PickupPointProductStockRow,
  currentPickupPointId: number,
  pickupPoints: Array<{ id: number }>,
) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const persist = useCallback(
    async (updatesForCurrent: { quantity: number; unlimitedStock: boolean }) => {
      if (!pickupPoints.length) {
        notifications.show({
          title: 'Нет точек',
          message: 'Не удалось загрузить список точек самовывоза',
          color: 'orange',
        });
        return;
      }
      setSaving(true);
      try {
        const product = await queryClient.fetchQuery({
          queryKey: ['product', row.productId],
          queryFn: () => productsApi.getById(row.productId),
        });
        const list = product.pickupStocks ?? [];
        const byPid = new Map(list.map((s) => [s.pickupPointId, s]));
        const rows: ProductPickupStockFormRow[] = pickupPoints.map((pp) => {
          const existing = byPid.get(pp.id);
          if (pp.id === currentPickupPointId) {
            return {
              pickupPointId: pp.id,
              quantity: updatesForCurrent.unlimitedStock
                ? 0
                : Math.max(0, updatesForCurrent.quantity),
              unlimitedStock: updatesForCurrent.unlimitedStock,
            };
          }
          return {
            pickupPointId: pp.id,
            quantity: existing?.quantity ?? 0,
            unlimitedStock: Boolean(existing?.unlimitedStock),
          };
        });
        await productsApi.updatePickupStocks(row.productId, rows);
        queryClient.invalidateQueries({ queryKey: ['product', row.productId] });
        queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
        notifications.show({
          title: 'Сохранено',
          message: 'Остаток обновлён',
          color: 'green',
        });
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : e instanceof Error
              ? e.message
              : 'Не удалось сохранить';
        notifications.show({
          title: 'Ошибка',
          message: String(msg ?? 'Не удалось сохранить'),
          color: 'red',
        });
      } finally {
        setSaving(false);
      }
    },
    [pickupPoints, currentPickupPointId, queryClient, row.productId],
  );

  return { persist, saving };
}

function usePickupStockRowControls(
  row: PickupPointProductStockRow,
  currentPickupPointId: number,
  pickupPoints: Array<{ id: number }>,
) {
  const { persist, saving } = usePickupStockPersist(row, currentPickupPointId, pickupPoints);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (row.unlimitedStock) {
      setDraft('');
      return;
    }
    if (!row.tracked) {
      setDraft('');
      return;
    }
    setDraft(String(row.quantity ?? 0));
  }, [row.productId, row.tracked, row.unlimitedStock, row.quantity]);

  const onBlurQuantity = () => {
    if (row.unlimitedStock) return;
    const n = draft.trim() === '' ? 0 : Math.max(0, Number(draft) || 0);
    if (!row.tracked) {
      if (draft.trim() === '') return;
      void persist({ quantity: n, unlimitedStock: false });
      return;
    }
    if (n === (row.quantity ?? 0)) return;
    void persist({ quantity: n, unlimitedStock: false });
  };

  const onToggleUnlimited = (checked: boolean) => {
    void persist({
      quantity: row.quantity ?? 0,
      unlimitedStock: checked,
    });
  };

  return {
    draft,
    setDraft,
    saving,
    onBlurQuantity,
    onToggleUnlimited,
    displayZero: row.tracked && !row.unlimitedStock && (row.quantity ?? 0) === 0,
    qtyDisabled: saving || row.unlimitedStock,
    hasPoints: pickupPoints.length > 0,
  };
}

function PickupStockTableRow({
  row,
  currentPickupPointId,
  pickupPoints,
}: {
  row: PickupPointProductStockRow;
  currentPickupPointId: number;
  pickupPoints: Array<{ id: number }>;
}) {
  const {
    draft,
    setDraft,
    saving,
    onBlurQuantity,
    onToggleUnlimited,
    displayZero,
    qtyDisabled,
    hasPoints,
  } = usePickupStockRowControls(row, currentPickupPointId, pickupPoints);

  return (
    <tr>
      <td className="pickup-point-stocks-table__td">
        <div className="pickup-point-stocks-table__thumb">
          {row.imageUrl ? (
            <CachedImage src={row.imageUrl} alt="" loading="lazy" />
          ) : (
            <span className="pickup-point-stocks-table__thumb-placeholder">—</span>
          )}
        </div>
      </td>
      <td className="pickup-point-stocks-table__td pickup-point-stocks-table__td--name">
        <Link to={`/products/${row.productId}`} style={{ color: 'inherit' }}>
          {row.name}
        </Link>
      </td>
      <td className="pickup-point-stocks-table__td pickup-point-stocks-table__td--qty">
        {!hasPoints ? (
          <span style={{ color: '#868e96' }}>—</span>
        ) : row.unlimitedStock ? (
          <div className="pickup-point-stocks-table__qty-input-inner">
            <span className="pickup-point-stocks-table__qty-unlimited-label">Без ограничений</span>
          </div>
        ) : (
          <div className="pickup-point-stocks-table__qty-input-inner">
            <Input
              type="number"
              min={0}
              step={1}
              size="xs"
              w={96}
              className="pickup-point-stocks-table__qty-input"
              placeholder={!row.tracked ? 'Указать' : undefined}
              value={draft}
              onChange={(e) => {
                setDraft(e.currentTarget.value);
              }}
              onBlur={onBlurQuantity}
              disabled={qtyDisabled}
              styles={{
                input: {
                  fontWeight: displayZero ? 600 : undefined,
                  color: displayZero ? '#c92a2a' : undefined,
                  textAlign: 'right' as const,
                },
              }}
            />
          </div>
        )}
      </td>
      <td className="pickup-point-stocks-table__td pickup-point-stocks-table__td--unlimited">
        {!hasPoints ? (
          <span style={{ color: '#868e96' }}>—</span>
        ) : (
          <div className="pickup-point-stocks-table__unlimited-cell-inner">
            <Checkbox
              checked={Boolean(row.tracked && row.unlimitedStock)}
              disabled={saving}
              onChange={(e) => onToggleUnlimited(e.currentTarget.checked)}
              aria-label="Без ограничений на этой точке"
            />
          </div>
        )}
      </td>
    </tr>
  );
}

function PickupStockMobileCard({
  row,
  currentPickupPointId,
  pickupPoints,
}: {
  row: PickupPointProductStockRow;
  currentPickupPointId: number;
  pickupPoints: Array<{ id: number }>;
}) {
  const {
    draft,
    setDraft,
    saving,
    onBlurQuantity,
    onToggleUnlimited,
    displayZero,
    qtyDisabled,
    hasPoints,
  } = usePickupStockRowControls(row, currentPickupPointId, pickupPoints);

  return (
    <div className="pickup-point-stock-card">
      <div className="pickup-point-stock-card__top">
        <div className="pickup-point-stock-card__thumb">
          {row.imageUrl ? (
            <CachedImage src={row.imageUrl} alt="" loading="lazy" />
          ) : (
            <span className="pickup-point-stock-card__thumb-placeholder">—</span>
          )}
        </div>
        <div className="pickup-point-stock-card__info">
          <Link to={`/products/${row.productId}`} className="pickup-point-stock-card__name">
            {row.name}
          </Link>
          <Link to={`/products/${row.productId}`} className="pickup-point-stock-card__id">
            ID {row.productId}
          </Link>
        </div>
      </div>
      <div className="pickup-point-stock-card__controls">
        <div className="pickup-point-stock-card__qty">
          <span className="pickup-point-stock-card__label">Количество</span>
          {!hasPoints ? (
            <span className="pickup-point-stock-card__dash">—</span>
          ) : row.unlimitedStock ? (
            <span className="pickup-point-stock-card__unlimited-text">Без ограничений</span>
          ) : (
            <Input
              type="number"
              min={0}
              step={1}
              size="xs"
              className="pickup-point-stocks-table__qty-input pickup-point-stock-card__qty-input"
              placeholder={!row.tracked ? 'Указать' : undefined}
              value={draft}
              onChange={(e) => {
                const next = e.currentTarget.value;
                setDraft(next);
              }}
              onBlur={onBlurQuantity}
              disabled={qtyDisabled}
              styles={{
                input: {
                  fontWeight: displayZero ? 600 : undefined,
                  color: displayZero ? '#c92a2a' : undefined,
                  textAlign: 'right' as const,
                },
              }}
            />
          )}
        </div>
        <label className="pickup-point-stock-card__unlimited">
          <Checkbox
            checked={Boolean(hasPoints && row.tracked && row.unlimitedStock)}
            disabled={!hasPoints || saving}
            onChange={(e) => onToggleUnlimited(e.currentTarget.checked)}
            aria-label="Без ограничений на этой точке"
          />
          <span>Без ограничений</span>
        </label>
      </div>
    </div>
  );
}

type PickupPointLocationState = { showNewPickupHints?: boolean };

/** Leaflet считает размер контейнера при монтировании; вкладка Mantine скрыта через display:none → нужно invalidateSize после показа. */
function InvalidateLeafletSizeWhenVisible({ visible }: { visible: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!visible) return;
    const fix = () => map.invalidateSize({ animate: false });
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(fix);
    });
    const t = window.setTimeout(fix, 150);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(t);
    };
  }, [visible, map]);
  return null;
}

export default function PickupPointDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isCompactSchedule = useIsMobile('(max-width: 900px)');
  const pickupPointId = id ? parseInt(id, 10) : 0;
  const { data: sellerApp } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const owner = isSellerOwner(sellerApp);
  const canManageContacts = sellerHasSection(sellerApp, 'CONTACTS');
  const canManageStocks = sellerHasSection(sellerApp, 'PRODUCTS');
  const stocksOnly = Boolean(sellerApp) && !owner && canManageStocks && !canManageContacts;
  const hideStocksTab = Boolean(sellerApp) && !owner && canManageContacts && !canManageStocks;
  const { data: pickupPoint, isLoading, error, refetch, isFetching } = usePickupPoint(pickupPointId);
  const { data: productStocksPayload, isLoading: productStocksLoading } =
    usePickupPointProductStocks(pickupPointId, canManageStocks);
  const productStockItems = productStocksPayload?.items ?? [];
  const { data: sellerPickupPoints = [] } = usePickupPoints();
  const updatePickupPoint = useUpdatePickupPoint();
  const addException = useAddException();
  const updateException = useUpdateException();
  const deleteException = useDeleteException();
  const deletePickupPoint = useDeletePickupPoint();
  const [isDeletePickupModalOpen, setIsDeletePickupModalOpen] = useState(false);
  const [zoneIdPendingDelete, setZoneIdPendingDelete] = useState<string | null>(null);
  const [exceptionDraft, setExceptionDraft] = useState<{
    id: string;
    index: number;
    date: Date | null;
    from: string;
    to: string;
    reason: string;
    enabled: boolean;
  } | null>(null);
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<
    Array<{
      id: string;
      day: string;
      from: string;
      to: string;
      allDay: boolean;
      enabled: boolean;
    }>
  | null>(null);
  const [showNewPickupHintsModal, setShowNewPickupHintsModal] = useState(false);
  const [deliveryProvider, setDeliveryProvider] = useState<PickupPointDeliveryProvider>('MARKETPLACE');
  const [zones, setZones] = useState<SellerDeliveryZone[]>([]);
  const [zonePrice, setZonePrice] = useState('');
  const [zoneDeliveryFrom, setZoneDeliveryFrom] = useState('');
  const [zoneDeliveryTo, setZoneDeliveryTo] = useState('');
  const [draftPolygon, setDraftPolygon] = useState<Array<{ lat: number; lng: number }>>([]);
  const [isDrawingZone, setIsDrawingZone] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedLatLng, setSelectedLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [clientDeliveryCompensationEnabled, setClientDeliveryCompensationEnabled] = useState(false);
  const [clientDeliveryCompensationPercent, setClientDeliveryCompensationPercent] = useState('0');
  const [detailTab, setDetailTab] = useState<string | null>('contacts');

  useEffect(() => {
    if (!sellerApp || !pickupPointId) return;
    if (!sellerCanAccessPickupPointId(sellerApp, pickupPointId)) {
      navigate('/contacts', { replace: true });
    }
  }, [sellerApp, pickupPointId, navigate]);

  useEffect(() => {
    if (stocksOnly) {
      setDetailTab('stocks');
      return;
    }
    if (hideStocksTab && detailTab === 'stocks') {
      setDetailTab('contacts');
    }
  }, [stocksOnly, hideStocksTab, detailTab]);

  const getContent = () => {
    // Только первичная загрузка — иначе при refetch после сохранения контент с Tabs размонтируется и вкладка сбрасывается на первую.
    if (isLoading) {
      return (
        <div className="content-wrapper content-wrapper--centered">
          <Loader />
        </div>
      );
    }

    if (error && refetch) {
      return (
        <div className="content-wrapper">
          <RefetchButton
            refetch={refetch}
            isLoading={isLoading || isFetching}
          />
        </div>
      );
    }

    return (
      null
    );
  };

  useEffect(() => {
    if (!pickupPoint) return;
    setDeliveryProvider(pickupPoint.deliveryProvider ?? 'MARKETPLACE');
    setZones(pickupPoint.sellerDeliveryZones ?? []);
    setClientDeliveryCompensationEnabled(
      pickupPoint.clientDeliveryCompensationEnabled ?? false,
    );
    setClientDeliveryCompensationPercent(
      String(pickupPoint.clientDeliveryCompensationPercent ?? 0),
    );
  }, [pickupPoint]);

  useEffect(() => {
    const state = location.state as PickupPointLocationState | undefined;
    if (!state?.showNewPickupHints) return;
    setShowNewPickupHintsModal(true);
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: {} },
    );
  }, [location.state, location.pathname, location.search, location.hash, navigate]);

  const {
    control: contactControl,
    handleSubmit: handleContactSubmit,
    reset: resetContactForm,
    watch: watchContact,
    setValue: setContactValue,
    formState: { isDirty: isContactFormDirty },
  } = useForm<PickupPointDetailsContactFormValues>({
    resolver: yupResolver(pickupPointDetailsContactSchema),
    defaultValues: {
      metro: '',
      metroIconColor: '',
      phoneNumber: '',
      email: '',
    },
  });

  useEffect(() => {
    if (!pickupPoint) return;
    resetContactForm({
      metro: pickupPoint.metro ?? '',
      metroIconColor: pickupPoint.metroIconColor ?? '',
      phoneNumber: pickupPoint.phoneNumber ? formatPickupPhoneForInput(pickupPoint.phoneNumber) : '',
      email: pickupPoint.email ?? '',
    });
  }, [pickupPoint, resetContactForm]);

  useEffect(() => {
    if (!pickupPoint) return;
    if (!hasMetroCatalog(pickupPoint.city)) return;
    if ((pickupPoint.metro ?? '').trim()) return;
    void applyNearestMetroToForm({
      lat: pickupPoint.lat,
      lon: pickupPoint.long,
      city: pickupPoint.city,
      setValue: setContactValue,
      onlyIfEmpty: true,
      currentMetro: '',
    });
  }, [pickupPoint, setContactValue]);

  const onSaveContacts = handleContactSubmit(async (values) => {
    if (!pickupPoint) return;
    const withMetro = hasMetroCatalog(pickupPoint.city);
    await updatePickupPoint.mutateAsync({
      id: pickupPointId,
      data: {
        metro: withMetro ? values.metro || undefined : '',
        metroIconColor: withMetro ? values.metroIconColor || undefined : '',
        phoneNumber: values.phoneNumber.replace(/\D/g, ''),
        email: values.email.trim(),
      },
    });
  });

  const mapCenter = useMemo<[number, number]>(() => {
    if (!pickupPoint) return [55.751244, 37.618423];
    return [pickupPoint.lat, pickupPoint.long];
  }, [pickupPoint]);

  /** Нет ни одного дня с включённым «Работает» и заполненными «С» / «До». */
  const scheduleHasNoWorkingDay = useMemo(() => {
    const wh = pickupPoint?.workingHours;
    if (wh == null || typeof wh !== 'object') return true;
    return !DAYS_OF_WEEK.some((day) => {
      const d = wh[day.key];
      if (!d?.enabled) return false;
      const from = String(d.from ?? '').trim();
      const to = String(d.to ?? '').trim();
      return Boolean(from && to);
    });
  }, [pickupPoint?.workingHours]);

  /** Доставка силами продавца без полигонов зон. */
  const sellerDeliveryMissingZones = useMemo(() => {
    const pp = pickupPoint;
    if (!pp) return false;
    if (pp.deliveryProvider !== 'SELLER') return false;
    const zones = pp.sellerDeliveryZones;
    return !zones || zones.length === 0;
  }, [pickupPoint?.deliveryProvider, pickupPoint?.sellerDeliveryZones]);

  /** Есть учёт по точке, но у всех таких товаров количество 0 и нет «без ограничений». */
  const pickupPointAllTrackedStocksZero = useMemo(() => {
    const items = productStockItems;
    if (!items.length) return false;
    const tracked = items.filter((r) => r.tracked);
    if (!tracked.length) return false;
    return tracked.every((r) => !r.unlimitedStock && (r.quantity ?? 0) === 0);
  }, [productStockItems]);

  const getZoneColor = (zone: SellerDeliveryZone) => {
    const color = ZONE_COLOR_OPTIONS.find((c) => c.value === zone.color);
    return color?.hex ?? '#8B7CF6';
  };

  const polygonArea = (polygon: Array<{ lat: number; lng: number }>): number => {
    let sum = 0;
    for (let i = 0; i < polygon.length; i += 1) {
      const j = (i + 1) % polygon.length;
      sum += polygon[i].lng * polygon[j].lat - polygon[j].lng * polygon[i].lat;
    }
    return Math.abs(sum) / 2;
  };

  const isPointInPolygon = (
    point: { lat: number; lng: number },
    polygon: Array<{ lat: number; lng: number }>,
  ): boolean => {
    if (polygon.length < 3) return false;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lng;
      const yi = polygon[i].lat;
      const xj = polygon[j].lng;
      const yj = polygon[j].lat;
      const intersects =
        yi > point.lat !== yj > point.lat &&
        point.lng < ((xj - xi) * (point.lat - yi)) / ((yj - yi) || Number.EPSILON) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  };

  // В Leaflet последний слой оказывается "выше". Рисуем большие зоны первыми,
  // чтобы меньшие (внутренние) были сверху и корректно ловили hover.
  const zonesForRender = useMemo(
    () =>
      [...zones]
        .filter((zone) => zone.id !== editingZoneId)
        .sort((a, b) => polygonArea(b.polygon) - polygonArea(a.polygon)),
    [zones, editingZoneId],
  );
  const selectedZone = selectedZoneId
    ? (zones.find((zone) => zone.id === selectedZoneId) ?? null)
    : null;

  const findInnermostZone = (lat: number, lng: number): SellerDeliveryZone | null => {
    let selected: { zone: SellerDeliveryZone; area: number } | null = null;
    for (const zone of zones) {
      if (!isPointInPolygon({ lat, lng }, zone.polygon)) continue;
      const area = polygonArea(zone.polygon);
      if (!selected || area < selected.area) {
        selected = { zone, area };
      }
    }
    return selected?.zone ?? null;
  };

  if (!pickupPoint) return getContent()

  function DeliveryZonesMapEditor() {
    const map = useMap();
    useEffect(() => {
      map.getContainer().style.cursor = isDrawingZone ? 'crosshair' : '';
      return () => {
        map.getContainer().style.cursor = '';
      };
    }, [map, isDrawingZone]);

    useMapEvents({
      click: (e) => {
        if (isDrawingZone) {
          setDraftPolygon((prev) => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
          return;
        }
        const zone = findInnermostZone(e.latlng.lat, e.latlng.lng);
        if (!zone) {
          setSelectedZoneId(null);
          setSelectedLatLng(null);
          return;
        }
        setSelectedZoneId(zone.id);
        setSelectedLatLng({ lat: e.latlng.lat, lng: e.latlng.lng });
      },
      mousemove: (e) => {
        if (isDrawingZone) return;
        const zone = findInnermostZone(e.latlng.lat, e.latlng.lng);
        if (!zone) {
          return;
        }
      },
    });
    return null;
  }

  const handleBack = () => {
    navigate('/contacts');
  };

  const confirmDeletePickup = async () => {
    await deletePickupPoint.mutateAsync(pickupPointId);
    setIsDeletePickupModalOpen(false);
    navigate('/contacts');
  };

  const zonePendingDelete = zoneIdPendingDelete
    ? zones.find((z) => z.id === zoneIdPendingDelete)
    : null;

  const confirmDeleteZone = () => {
    if (!zoneIdPendingDelete) return;
    const previousZones = zones;
    const nextZones = zones.filter((z) => z.id !== zoneIdPendingDelete);
    setZones(nextZones);
    setSelectedZoneId(null);
    setSelectedLatLng(null);
    setZoneIdPendingDelete(null);
    void saveDeliverySettings('SELLER', nextZones).then((result) => {
      if (!result.ok) {
        setZones(previousZones);
        notifications.show({
          title: 'Ошибка',
          message: result.message ?? 'Не удалось обновить настройки доставки',
          color: 'red',
        });
      }
    });
  };

  const saveDeliverySettings = async (
    nextDeliveryProvider: PickupPointDeliveryProvider,
    nextZones: SellerDeliveryZone[],
    compensation?: { enabled: boolean; percent: number },
  ): Promise<{ ok: boolean; message?: string }> => {
    const normalizedZones = nextZones.map((zone, index) => ({
      ...zone,
      color: zone.color ?? ZONE_COLOR_ORDER[index % ZONE_COLOR_ORDER.length],
    }));
    const enabled =
      compensation?.enabled ?? clientDeliveryCompensationEnabled;
    const percent =
      compensation?.percent ?? Number(clientDeliveryCompensationPercent);
    try {
      await updatePickupPoint.mutateAsync({
        id: pickupPointId,
        silent: true,
        data: {
          sellerDeliveryZones: nextDeliveryProvider === 'SELLER' ? normalizedZones : [],
          clientDeliveryCompensationEnabled:
            nextDeliveryProvider === 'MARKETPLACE' ? enabled : false,
          clientDeliveryCompensationPercent:
            nextDeliveryProvider === 'MARKETPLACE'
              ? Math.max(0, Math.min(100, percent))
              : 0,
        },
      });
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: getPickupPointMutationErrorMessage(
          error,
          'Не удалось обновить настройки доставки',
        ),
      };
    }
  };

  // Подготовка данных для таблицы расписания работы
  const ALL_DAY_FROM = '00:00';
  const ALL_DAY_TO = '23:59';
  const isAllDayHours = (from?: string, to?: string) => {
    const fromNorm = from?.trim() ?? '';
    const toNorm = to?.trim() ?? '';
    return fromNorm === ALL_DAY_FROM && (toNorm === ALL_DAY_TO || toNorm === '24:00');
  };

  const workingHoursRows = DAYS_OF_WEEK.map((day) => {
    const dayData = pickupPoint.workingHours?.[day.key] || { from: '', to: '', enabled: false };
    return {
      id: day.key,
      day: isCompactSchedule ? day.shortLabel : day.label,
      from: dayData.from,
      to: dayData.to,
      allDay: isAllDayHours(dayData.from, dayData.to),
      enabled: dayData.enabled,
    };
  });

  const workingHoursColumns: EditableDataTableColumn<(typeof workingHoursRows)[number]>[] = [
    {
      key: 'day',
      label: isMobile ? 'День' : 'День недели',
      editable: false,
      style: isMobile ? { width: '36px' } : undefined,
    },
    {
      key: 'from',
      label: 'С',
      editable: true,
      type: 'time',
      style: isMobile ? { width: '30%' } : undefined,
      render: (item, isEditing, value, onChange) => {
        if (!isEditing) {
          return <span>{value || '-'}</span>;
        }
        return (
          <ScheduleTimeInput
            value={value ?? ''}
            onChange={onChange}
            disabled={item.allDay}
            className="editable-data-table__input editable-data-table__input--time"
          />
        );
      },
    },
    {
      key: 'to',
      label: 'До',
      editable: true,
      type: 'time',
      style: isMobile ? { width: '30%' } : undefined,
      render: (item, isEditing, value, onChange) => {
        if (!isEditing) {
          return <span>{value || '-'}</span>;
        }
        return (
          <ScheduleTimeInput
            value={value ?? ''}
            onChange={onChange}
            disabled={item.allDay}
            className="editable-data-table__input editable-data-table__input--time"
          />
        );
      },
    },
    {
      key: 'allDay',
      label: isCompactSchedule ? '24ч' : 'Круглосуточно',
      editable: true,
      style: isMobile ? { width: '40px' } : isCompactSchedule ? { width: '48px' } : undefined,
      render: (_item, isEditing, value, onChange) => {
        if (!isEditing) {
          return <span>{value ? 'Да' : 'Нет'}</span>;
        }
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={(e) => onChange(e.currentTarget.checked)}
            aria-label="Круглосуточно"
          />
        );
      },
    },
    {
      key: 'enabled',
      label: isMobile ? 'Вкл' : 'Работает',
      editable: true,
      type: 'switch',
      style: isMobile ? { width: '48px' } : undefined,
      render: (item, isEditing, value, onChange) => {
        if (!isEditing) {
          return <span>{value ? 'Да' : 'Нет'}</span>;
        }
        const hasTimes = Boolean(item.from?.trim() && item.to?.trim());
        return (
          <Tooltip
            label="Сначала укажите время «С» и «До»"
            disabled={hasTimes}
            withArrow
            position="top"
            openDelay={180}
            multiline
            w={220}
          >
            <span className="pickup-point-details-switch-wrap">
              <Switch
                checked={Boolean(value)}
                disabled={!hasTimes}
                onChange={(e) => {
                  const next = e.currentTarget.checked;
                  if (next && !hasTimes) return;
                  onChange(next);
                }}
                size="xs"
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const handleWorkingHoursEditableUpdate = ({
    prev,
    itemId,
    columnKey,
    nextItem,
  }: {
    prev: typeof workingHoursRows;
    itemId: number | string;
    columnKey: string;
    value: unknown;
    nextItem: (typeof workingHoursRows)[number];
  }) => {
    let item = nextItem;

    if (columnKey === 'allDay') {
      if (item.allDay) {
        item = {
          ...item,
          from: ALL_DAY_FROM,
          to: ALL_DAY_TO,
          enabled: true,
          allDay: true,
        };
      } else {
        item = { ...item, allDay: false };
      }
    }

    if (columnKey === 'enabled' && item.enabled) {
      if (!item.from?.trim() || !item.to?.trim()) {
        notifications.show({
          title: 'Ошибка',
          message: 'Сначала укажите время «С» и «До»',
          color: 'red',
        });
        item = { ...item, enabled: false };
      }
    }

    if (columnKey === 'enabled' && !item.enabled) {
      item = { ...item, allDay: false };
    }

    if (columnKey === 'from' || columnKey === 'to') {
      item = { ...item, allDay: isAllDayHours(item.from, item.to) };
      if (item.enabled && (!item.from?.trim() || !item.to?.trim())) {
        item = { ...item, enabled: false, allDay: false };
      }
    }

    const first = prev[0];
    if (!first) {
      return prev.map((row) => (row.id === itemId ? item : row));
    }

    if (itemId === first.id) {
      return prev.map((row) =>
        row.id === itemId
          ? item
          : {
              ...row,
              from: item.from,
              to: item.to,
              enabled: item.enabled,
              allDay: item.allDay,
            },
      );
    }

    return prev.map((row) => (row.id === itemId ? item : row));
  };

  const handleSaveWorkingHours = async (updatedData: typeof workingHoursRows) => {
    const invalidDay = updatedData.find(
      (row) => row.enabled && (!row.from?.trim() || !row.to?.trim()),
    );
    if (invalidDay) {
      notifications.show({
        title: 'Ошибка',
        message: `Для «${invalidDay.day}» укажите время «С» и «До» или выключите день`,
        color: 'red',
      });
      throw new Error('working hours require from/to when enabled');
    }

    const workingHours: WorkingHours = {};
    updatedData.forEach((row) => {
      workingHours[row.id] = {
        from: row.from,
        to: row.to,
        enabled: row.enabled,
      };
    });
    try {
      await updatePickupPoint.mutateAsync({
        id: pickupPointId,
        data: { workingHours },
        silent: true,
      });
      notifications.show({
        title: 'Успешно',
        message: 'Расписание работы обновлено',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при обновлении расписания',
        ),
        color: 'red',
      });
      throw error;
    }
  };

  const startScheduleEdit = () => {
    setScheduleDraft(workingHoursRows.map((row) => ({ ...row })));
    setScheduleEditing(true);
  };

  const cancelScheduleEdit = () => {
    setScheduleDraft(null);
    setScheduleEditing(false);
  };

  const updateScheduleDraftField = (
    itemId: string,
    columnKey: 'from' | 'to' | 'allDay' | 'enabled',
    value: string | boolean,
  ) => {
    setScheduleDraft((prev) => {
      if (!prev) return prev;
      const current = prev.find((row) => row.id === itemId);
      if (!current) return prev;
      const nextItem = { ...current, [columnKey]: value };
      return handleWorkingHoursEditableUpdate({
        prev,
        itemId,
        columnKey,
        value,
        nextItem,
      });
    });
  };

  const saveScheduleDraft = async () => {
    if (!scheduleDraft) return;
    try {
      await handleSaveWorkingHours(scheduleDraft);
      setScheduleDraft(null);
      setScheduleEditing(false);
    } catch {
      // ошибка уже показана
    }
  };

  // Подготовка данных для таблицы настроек слотов
  const slotsRows = [
    {
      id: 'slotDuration',
      name: 'Длительность слота (минуты)',
      value: pickupPoint.slotDuration ?? 60,
    },
    {
      id: 'maxOrdersPerSlot',
      name: 'Максимум заказов в слот',
      value: pickupPoint.maxOrdersPerSlot ?? 4,
    },
    {
      id: 'advanceDays',
      name: 'Дней вперед для бронирования',
      value: pickupPoint.advanceDays ?? 14,
    },
    // Глобально через env MIN_HOURS_BEFORE_BOOKING (не настройка точки)
    // {
    //   id: 'minHoursBefore',
    //   name: 'Минимум часов до бронирования',
    //   value: pickupPoint.minHoursBefore ?? 1,
    // },
  ];

  const slotsColumns: EditableDataTableColumn<(typeof slotsRows)[number]>[] = [
    {
      key: 'name',
      label: 'Настройка',
      editable: false,
      style: isMobile ? { width: 'auto' } : undefined,
      render: (item) => <SlotSettingName id={item.id} name={item.name} />,
    },
    {
      key: 'value',
      label: 'Значение',
      editable: true,
      type: 'number',
      style: isMobile ? { width: 96 } : undefined,
      render: (item, isEditing, value, onChange) => {
        if (!isEditing) {
          return <span>{value === '' || value == null ? '-' : value}</span>;
        }
        return (
          <Input
            type="number"
            min={item.id === 'slotDuration' ? 60 : 1}
            value={value === '' || value == null ? '' : value}
            onChange={(e) => {
              const raw = e.currentTarget.value;
              onChange(raw === '' ? '' : Number(raw));
            }}
            size="xs"
            className="editable-data-table__input editable-data-table__input--number"
          />
        );
      },
    },
  ];

  const handleSaveSlots = async (updatedData: typeof slotsRows) => {
    const slotDurationRow = updatedData.find((row) => row.id === 'slotDuration');
    const slotDuration = Number(slotDurationRow?.value);
    if (!Number.isFinite(slotDuration) || slotDuration < 60) {
      notifications.show({
        title: 'Ошибка',
        message: 'Длительность слота не может быть меньше 60 минут',
        color: 'red',
      });
      throw new Error('slotDuration must be at least 60');
    }

    const maxOrdersRow = updatedData.find((row) => row.id === 'maxOrdersPerSlot');
    const maxOrders = Number(maxOrdersRow?.value);
    if (!Number.isFinite(maxOrders) || maxOrders < 1) {
      notifications.show({
        title: 'Ошибка',
        message: 'Максимум заказов в слот не может быть меньше 1',
        color: 'red',
      });
      throw new Error('maxOrdersPerSlot must be at least 1');
    }

    const advanceDaysRow = updatedData.find((row) => row.id === 'advanceDays');
    const advanceDays = Number(advanceDaysRow?.value);
    if (!Number.isFinite(advanceDays) || advanceDays < 1) {
      notifications.show({
        title: 'Ошибка',
        message: 'Число дней вперёд для бронирования не может быть меньше 1',
        color: 'red',
      });
      throw new Error('advanceDays must be at least 1');
    }

    const slotsData: Record<string, number> = {
      slotDuration,
      maxOrdersPerSlot: maxOrders,
      advanceDays,
    };
    try {
      await updatePickupPoint.mutateAsync({
        id: pickupPointId,
        data: slotsData,
        silent: true,
      });
      notifications.show({
        title: 'Успешно',
        message: 'Настройки слотов обновлены',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при обновлении настроек слотов',
        ),
        color: 'red',
      });
      throw error;
    }
  };

  const handleTimezoneChange = async (value: string | null) => {
    if (!value || value === pickupPoint.timezone) return;
    try {
      await updatePickupPoint.mutateAsync({
        id: pickupPointId,
        data: { timezone: value },
        silent: true,
      });
      notifications.show({
        title: 'Успешно',
        message: 'Часовой пояс точки обновлён',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Не удалось обновить часовой пояс',
        ),
        color: 'red',
      });
    }
  };

  // Подготовка данных для таблицы исключений
  // Используем индекс в id для идентификации исключений
  const exceptionsRows = (pickupPoint.exceptions || []).map((exc, index) => ({
    id: `exception-${index}`, // id содержит индекс для идентификации
    index, // Сохраняем индекс для удобства
    date: exc.date ? new Date(exc.date) : null,
    from: exc.from || '',
    to: exc.to || '',
    reason: exc.reason || '',
    enabled: exc.enabled,
  }));

  const exceptionsColumns: EditableDataTableColumn[] = [
    {
      key: 'date',
      label: 'Дата',
      editable: true,
      type: 'date',
    },
    {
      key: 'from',
      label: 'С',
      editable: true,
      type: 'time',
    },
    {
      key: 'to',
      label: 'До',
      editable: true,
      type: 'time',
    },
    {
      key: 'reason',
      label: 'Причина',
      editable: true,
      type: 'text',
    },
    {
      key: 'enabled',
      label: 'Работает',
      editable: true,
      type: 'switch',
    },
  ];

  const handleSaveExceptions = async (updatedData: typeof exceptionsRows) => {
    const originalExceptions = pickupPoint.exceptions || [];

    // Разделяем строки на новые и существующие
    const newRows = updatedData.filter(row => String(row.id).startsWith('new-'));
    const existingRows = updatedData.filter(row => String(row.id).startsWith('exception-'));

    try {
      // 1. Сначала обрабатываем удаления (с конца, чтобы индексы не сдвигались)
      const existingIndices = new Set(
        existingRows.map(row => {
          const match = String(row.id).match(/^exception-(\d+)$/);
          return match ? parseInt(match[1], 10) : -1;
        }).filter(idx => idx >= 0)
      );

      for (let i = originalExceptions.length - 1; i >= 0; i--) {
        if (!existingIndices.has(i)) {
          await deleteException.mutateAsync({
            id: pickupPointId,
            exceptionIndex: i,
          });
        }
      }

      // 2. Затем добавляем новые исключения
      for (const newRow of newRows) {
        if (!newRow.date) {
          notifications.show({
            title: 'Ошибка',
            message: 'Дата обязательна для исключения',
            color: 'red',
          });
          throw new Error('Дата обязательна для исключения'); // строка остаётся в режиме создания
        }

        const exceptionData = {
          date: newRow.date instanceof Date
            ? newRow.date.toISOString().split('T')[0]
            : String(newRow.date).split('T')[0],
          from: newRow.from || undefined,
          to: newRow.to || undefined,
          reason: newRow.reason || undefined,
          enabled: newRow.enabled,
        };

        await addException.mutateAsync({
          id: pickupPointId,
          data: exceptionData,
        });
      }

      // 3. Наконец, обновляем существующие исключения
      // После удалений и добавлений нужно пересчитать индексы
      // Но мы используем исходные индексы из id
      for (const existingRow of existingRows) {
        const match = String(existingRow.id).match(/^exception-(\d+)$/);
        if (!match) continue;

        const originalIndex = parseInt(match[1], 10);
        const originalException = originalExceptions[originalIndex];

        if (!originalException) continue;

        // Проверяем, изменилась ли строка
        const dateStr = existingRow.date instanceof Date
          ? existingRow.date.toISOString().split('T')[0]
          : (existingRow.date ? String(existingRow.date).split('T')[0] : '');

        const hasChanges =
          dateStr !== originalException.date ||
          (existingRow.from || '') !== (originalException.from || '') ||
          (existingRow.to || '') !== (originalException.to || '') ||
          (existingRow.reason || '') !== (originalException.reason || '') ||
          existingRow.enabled !== originalException.enabled;

        if (hasChanges) {
          const exceptionData = {
            date: dateStr || undefined,
            from: existingRow.from || undefined,
            to: existingRow.to || undefined,
            reason: existingRow.reason || undefined,
            enabled: existingRow.enabled,
          };

          await updateException.mutateAsync({
            id: pickupPointId,
            exceptionIndex: originalIndex,
            data: exceptionData,
          });
        }
      }
    } catch (error) {
      console.error('Error saving exceptions:', error);
      throw error; // Пробрасываем, чтобы строка осталась в режиме редактирования
    }
  };

  const getNewExceptionRow = (): typeof exceptionsRows[0] => ({
    id: `new-${Date.now()}`,
    index: -1, // Временный индекс для новых строк
    date: null,
    from: '',
    to: '',
    reason: '',
    enabled: false,
  });

  const exceptionsBusy =
    addException.isPending || updateException.isPending || deleteException.isPending;

  const formatExceptionDate = (date: Date | null) => {
    if (!date) return '—';
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const startExceptionEdit = (row: (typeof exceptionsRows)[number]) => {
    setExceptionDraft({ ...row, date: row.date ? new Date(row.date) : null });
  };

  const startExceptionCreate = () => {
    setExceptionDraft(getNewExceptionRow());
  };

  const cancelExceptionEdit = () => {
    setExceptionDraft(null);
  };

  const saveExceptionDraft = async () => {
    if (!exceptionDraft) return;
    const isNew = String(exceptionDraft.id).startsWith('new-');
    const nextRows = isNew
      ? [...exceptionsRows, exceptionDraft]
      : exceptionsRows.map((row) => (row.id === exceptionDraft.id ? exceptionDraft : row));
    try {
      await handleSaveExceptions(nextRows);
      setExceptionDraft(null);
    } catch {
      // строка остаётся в режиме редактирования
    }
  };

  const deleteExceptionRow = async (rowId: string) => {
    try {
      await handleSaveExceptions(exceptionsRows.filter((row) => row.id !== rowId));
      if (exceptionDraft?.id === rowId) setExceptionDraft(null);
    } catch {
      // ignore
    }
  };




  return (
    <div className="page-container pickup-point-details-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Точки самовывоза', path: '/contacts' },
          { label: pickupPoint.address },
        ]}
      />

      <div className="page-header">
        <div className="page-header-top">
          <div className="page-header-title">
            <BackButton onClick={handleBack} />
            <h1>{pickupPoint.address}</h1>
          </div>
          <div className="page-header-actions">
            {owner ? (
              <button
                type="button"
                className="pickup-point-details-delete-link"
                onClick={() => setIsDeletePickupModalOpen(true)}
                disabled={deletePickupPoint.isPending}
              >
                {deletePickupPoint.isPending ? 'Удаление…' : 'Удалить'}
              </button>
            ) : null}
          </div>
        </div>
        {(!stocksOnly &&
          (scheduleHasNoWorkingDay ||
            sellerDeliveryMissingZones ||
            pickupPointAllTrackedStocksZero)) ||
          (stocksOnly && pickupPointAllTrackedStocksZero) ? (
          <Alert
            className="pickup-point-details-alert"
            tone="error"
            title="Магазин не работает!"
          >
            <ul className="pickup-point-details-alert-list">
              {!stocksOnly && scheduleHasNoWorkingDay && (
                <li>
                  Для работы точки самовывоза укажите расписание на вкладке «Расписание».
                </li>
              )}
              {!stocksOnly && sellerDeliveryMissingZones && (
                <li>
                  Выбрана доставка силами продавца — на вкладке «Доставка» добавьте хотя бы одну зону на карте.
                </li>
              )}
              {canManageStocks && pickupPointAllTrackedStocksZero && (
                <li>Пополните остатки на вкладке «Остатки»</li>
              )}
            </ul>
          </Alert>
        ) : null}
      </div>

      {getContent() ? getContent() : <div className="content-wrapper pickup-point-details-content">
        <Tabs
          value={detailTab}
          onChange={setDetailTab}
          className="pickup-point-details-tabs"
        >
          <Tabs.List className="pickup-point-details-tabs-list">
            {canManageContacts ? (
              <>
                <Tabs.Tab value="contacts" className="pickup-point-details-tabs-tab">
                  Контакты
                </Tabs.Tab>
                <Tabs.Tab value="delivery" className="pickup-point-details-tabs-tab">
                  Доставка
                </Tabs.Tab>
                <Tabs.Tab value="schedule" className="pickup-point-details-tabs-tab">
                  Расписание
                </Tabs.Tab>
                <Tabs.Tab value="exceptions" className="pickup-point-details-tabs-tab">
                  Исключения
                </Tabs.Tab>
                <Tabs.Tab value="slots" className="pickup-point-details-tabs-tab">
                  Слоты
                </Tabs.Tab>
              </>
            ) : null}
            {canManageStocks ? (
              <Tabs.Tab value="stocks" className="pickup-point-details-tabs-tab">
                Остатки
              </Tabs.Tab>
            ) : null}
          </Tabs.List>

          <Tabs.Panel value="contacts" pt="md" className="pickup-point-details-tab-panel">
            <div className="pickup-point-details-section">
              <h3 className="pickup-point-details-section__title">Контакты и адрес</h3>

              <form noValidate onSubmit={onSaveContacts} className="pickup-point-details-form">
                <div className="pickup-point-details-fields pickup-point-details-fields--half">
                  <div className="pickup-point-details-field pickup-point-details-field--half">
                    <Input label="Город" value={pickupPoint.city} disabled readOnly />
                  </div>
                  {hasMetroCatalog(pickupPoint.city) ? (
                    <div className="pickup-point-details-field pickup-point-details-field--half">
                      <PickupPointMetroFields
                        city={pickupPoint.city}
                        visible
                        setValue={setContactValue}
                        watch={watchContact}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="pickup-point-details-fields">
                  <div className="pickup-point-details-field pickup-point-details-field--full">
                    <Input
                      label="Адрес"
                      value={pickupPoint.address}
                      disabled
                      readOnly
                    />
                  </div>
                </div>

                <div className="pickup-point-details-fields pickup-point-details-fields--end">
                  <div className="pickup-point-details-field">
                    <ControlledInput
                      label="Номер телефона"
                      name="phoneNumber"
                      control={contactControl}
                      formatOnChange={formatPickupPhoneForInput}
                      placeholder="+7 (XXX) XXX-XX-XX"
                      description="При экстренных ситуациях, важных вопросах и спорных моментах будем звонить на этот номер"
                    />
                  </div>
                  <div className="pickup-point-details-field">
                    <ControlledInput
                      label="Email"
                      type="text"
                      name="email"
                      control={contactControl}
                      placeholder="order@example.com"
                      description="На этот email будут приходить уведомления о новых заказах"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>
                </div>

                <div className="pickup-point-details-form__actions">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={updatePickupPoint.isPending}
                    disabled={!isContactFormDirty || updatePickupPoint.isPending}
                  >
                    Сохранить изменения
                  </Button>
                </div>
              </form>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="delivery" pt="md" className="pickup-point-details-tab-panel">
            <div className="pickup-point-details-section pickup-point-details-section--delivery">
              <h3 className="pickup-point-details-section__title">Настройки доставки</h3>

              <div className="pickup-point-details-delivery-layout">
                <div className="pickup-point-details-delivery-sidebar">
                  <RadioGroup
                    label=""
                    value={deliveryProvider}
                    onChange={() => undefined}
                  >
                    <div className="pickup-point-details-delivery-radios">
                      <Radio
                        value="MARKETPLACE"
                        label="Партнерами маркетплейса"
                        description={DELIVERY_OPTION_HINTS.MARKETPLACE}
                        disabled
                      />
                      <Radio
                        value="SELLER"
                        label="Силами продавца"
                        description={DELIVERY_OPTION_HINTS.SELLER}
                        disabled
                      />
                    </div>
                  </RadioGroup>
                  <p className="pickup-point-details-delivery-locked-hint">
                    Способ доставки выбирается при создании точки и не меняется. Зоны и компенсацию
                    можно редактировать ниже.
                  </p>
                  {deliveryProvider === 'MARKETPLACE' && (
                    <div className="pickup-point-details-delivery-extra">
                      <Switch
                        label="Компенсация доставки клиента"
                        description={DELIVERY_OPTION_HINTS.COMPENSATION}
                        checked={clientDeliveryCompensationEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setClientDeliveryCompensationEnabled(enabled);
                          const percent = Math.max(
                            0,
                            Math.min(100, Number(clientDeliveryCompensationPercent)),
                          );
                          void saveDeliverySettings('MARKETPLACE', [], {
                            enabled,
                            percent: Number.isFinite(percent) ? percent : 0,
                          }).then((result) => {
                            if (!result.ok) {
                              notifications.show({
                                title: 'Ошибка',
                                message:
                                  result.message ??
                                  'Не удалось обновить компенсацию доставки',
                                color: 'red',
                              });
                              setClientDeliveryCompensationEnabled((prev) => !prev);
                            }
                          });
                        }}
                      />
                      <div className="pickup-point-details-compensation-percent">
                        <Input
                          label="Процент компенсации (0-100)"
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={clientDeliveryCompensationPercent}
                          disabled={!clientDeliveryCompensationEnabled}
                          onChange={(e) =>
                            setClientDeliveryCompensationPercent(e.target.value)
                          }
                          onBlur={() => {
                            const parsed = Number(clientDeliveryCompensationPercent);
                            const normalized = Number.isFinite(parsed)
                              ? Math.max(0, Math.min(100, parsed))
                              : 0;
                            setClientDeliveryCompensationPercent(String(normalized));
                            void saveDeliverySettings('MARKETPLACE', [], {
                              enabled: clientDeliveryCompensationEnabled,
                              percent: normalized,
                            }).then((result) => {
                              if (!result.ok) {
                                notifications.show({
                                  title: 'Ошибка',
                                  message:
                                    result.message ??
                                    'Не удалось обновить процент компенсации',
                                  color: 'red',
                                });
                              }
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {deliveryProvider === 'SELLER' &&
                    (isDrawingZone || zones.length === 0 || zones.length >= 5) && (
                      <div className="pickup-point-details-delivery-extra">
                        {(isDrawingZone || zones.length === 0) && (
                          <div className="pickup-point-details-hint pickup-point-details-hint--steps">
                            {!isDrawingZone ? (
                              <>
                                <div>1. Нажмите «Добавить зону» на карте</div>
                                <div>2. Укажите цену и время доставки (от и до, в минутах)</div>
                                <div>3. Кликайте по карте и сохраните зону</div>
                              </>
                            ) : (
                              <>
                                <div>1. Укажите цену и время доставки (от и до, минуты)</div>
                                <div>2. Кликайте по карте, чтобы добавить точки</div>
                                <div>3. Нажмите сохранить</div>
                              </>
                            )}
                          </div>
                        )}
                        {zones.length >= 5 && (
                          <div className="pickup-point-details-hint pickup-point-details-hint--danger">
                            Достигнут лимит: максимум 5 зон доставки
                          </div>
                        )}
                        {zones.length === 0 && (
                          <div className="pickup-point-details-hint pickup-point-details-hint--danger pickup-point-details-hint--flush">
                            Для доставки своими силами добавьте хотя бы одну зону
                          </div>
                        )}
                      </div>
                    )}
                </div>
                {deliveryProvider === 'SELLER' && (
                  <div className="pickup-point-details-map-wrap">
                    <div className="pickup-point-details-map-overlay">
                      <div className="pickup-point-details-map-toolbar">
                        {!isDrawingZone ? (
                          <button
                            type="button"
                            className="pickup-point-details-add-zone-link"
                            onClick={() => {
                              setEditingZoneId(null);
                              setZonePrice('');
                              setZoneDeliveryFrom('');
                              setZoneDeliveryTo('');
                              setDraftPolygon([]);
                              setIsDrawingZone(true);
                            }}
                            disabled={zones.length >= 5}
                          >
                            <span className="pickup-point-details-add-zone-link__plus" aria-hidden>
                              +
                            </span>
                            <span className="pickup-point-details-add-zone-link__text">
                              Добавить зону
                            </span>
                          </button>
                        ) : (
                          <>
                            <Input
                              className="pickup-point-details-zone-price-input"
                              type="number"
                              min={0}
                              placeholder="Цена, ₽"
                              aria-label="Цена зоны в рублях"
                              value={zonePrice}
                              onChange={(e) => setZonePrice(e.target.value)}
                            />
                            <Input
                              className="pickup-point-details-zone-price-input"
                              type="number"
                              min={0}
                              step={1}
                              placeholder="От, мин"
                              aria-label="Время доставки от, минуты"
                              value={zoneDeliveryFrom}
                              onChange={(e) => setZoneDeliveryFrom(e.target.value)}
                            />
                            <Input
                              className="pickup-point-details-zone-price-input"
                              type="number"
                              min={0}
                              step={1}
                              placeholder="До, мин"
                              aria-label="Время доставки до, минуты"
                              value={zoneDeliveryTo}
                              onChange={(e) => setZoneDeliveryTo(e.target.value)}
                            />
                            <IconTextAction
                              icon={<IoSaveOutline size={18} />}
                              className="pickup-point-details-icon-only-action"
                              aria-label="Сохранить зону"
                              title="Сохранить зону"
                              onClick={() => {
                                const rawPrice = zonePrice.trim();
                                const price = Number(zonePrice);
                                const deliveryFrom = Number(zoneDeliveryFrom);
                                const deliveryTo = Number(zoneDeliveryTo);
                                const minutesOk =
                                  zoneDeliveryFrom.trim() !== '' &&
                                  zoneDeliveryTo.trim() !== '' &&
                                  Number.isInteger(deliveryFrom) &&
                                  Number.isInteger(deliveryTo) &&
                                  deliveryFrom >= 0 &&
                                  deliveryTo >= deliveryFrom;
                                if (
                                  !rawPrice ||
                                  !Number.isFinite(price) ||
                                  price < 0 ||
                                  !minutesOk ||
                                  draftPolygon.length < 3
                                ) {
                                  notifications.show({
                                    title: 'Недостаточно данных',
                                    message:
                                      'Укажите цену, время доставки (от и до, в минутах) и минимум 3 точки полигона',
                                    color: 'orange',
                                  });
                                  return;
                                }
                                const previousZones = zones;
                                const nextZones = editingZoneId
                                  ? zones.map((zone) =>
                                      zone.id === editingZoneId
                                        ? {
                                            ...zone,
                                            price,
                                            deliveryMinutesFrom: deliveryFrom,
                                            deliveryMinutesTo: deliveryTo,
                                            polygon: draftPolygon,
                                          }
                                        : zone,
                                    )
                                  : [
                                      ...zones,
                                      {
                                        id: crypto.randomUUID(),
                                        name: `Зона ${zones.length + 1}`,
                                        price,
                                        deliveryMinutesFrom: deliveryFrom,
                                        deliveryMinutesTo: deliveryTo,
                                        color:
                                          ZONE_COLOR_ORDER[
                                            zones.length % ZONE_COLOR_ORDER.length
                                          ],
                                        polygon: draftPolygon,
                                      } satisfies SellerDeliveryZone,
                                    ];
                                setZones(nextZones);
                                setZonePrice('');
                                setZoneDeliveryFrom('');
                                setZoneDeliveryTo('');
                                setDraftPolygon([]);
                                setEditingZoneId(null);
                                setIsDrawingZone(false);
                                void saveDeliverySettings('SELLER', nextZones).then((result) => {
                                  if (!result.ok) {
                                    setZones(previousZones);
                                    notifications.show({
                                      title: 'Ошибка',
                                      message: result.message ?? 'Не удалось обновить настройки доставки',
                                      color: 'red',
                                    });
                                  }
                                });
                              }}
                              disabled={!editingZoneId && zones.length >= 5}
                              variant="primary"
                            >
                              Сохранить
                            </IconTextAction>
                            <IconTextAction
                              icon={<IoArrowUndoOutline size={18} />}
                              className="pickup-point-details-icon-only-action"
                              aria-label="Удалить последнюю точку"
                              title="Удалить последнюю точку"
                              onClick={() => setDraftPolygon((prev) => prev.slice(0, -1))}
                              disabled={draftPolygon.length === 0}
                              variant="default"
                            >
                              Назад
                            </IconTextAction>
                            <IconTextAction
                              icon={<IoCloseOutline size={18} />}
                              className="pickup-point-details-icon-only-action"
                              aria-label="Отменить рисование"
                              title="Отменить рисование"
                              onClick={() => {
                                setIsDrawingZone(false);
                                setEditingZoneId(null);
                                setDraftPolygon([]);
                                setZonePrice('');
                                setZoneDeliveryFrom('');
                                setZoneDeliveryTo('');
                              }}
                              variant="default"
                            >
                              Отменить
                            </IconTextAction>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="pickup-point-details-map">
                      <MapContainer center={mapCenter} zoom={11} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <InvalidateLeafletSizeWhenVisible
                          visible={detailTab === 'delivery' && deliveryProvider === 'SELLER'}
                        />
                        <DeliveryZonesMapEditor />
                        <Marker position={mapCenter} icon={pickupPointMapIcon} />
                        {draftPolygon.length >= 3 && (
                          <Polygon
                            positions={draftPolygon.map((p) => [p.lat, p.lng] as [number, number])}
                            pathOptions={{ color: '#111827', dashArray: '6 4', fillOpacity: 0.1 }}
                          />
                        )}
                        {draftPolygon.map((point, index) => (
                          <CircleMarker
                            key={`draft-point-${index}`}
                            center={[point.lat, point.lng]}
                            radius={5}
                            pathOptions={{
                              color: '#111827',
                              fillColor: '#ffffff',
                              fillOpacity: 1,
                              weight: 2,
                            }}
                          />
                        ))}
                        {zonesForRender.map((zone) => {
                          const color = getZoneColor(zone);
                          return (
                            <Polygon
                              key={zone.id}
                              positions={zone.polygon.map((p) => [p.lat, p.lng] as [number, number])}
                              pathOptions={{
                                color,
                                fillColor: color,
                                fillOpacity: 0.2,
                                weight: 2,
                              }}
                            />
                          );
                        })}
                        {selectedZoneId && selectedLatLng && (
                          <Popup
                            position={[selectedLatLng.lat, selectedLatLng.lng]}
                            closeButton={false}
                            eventHandlers={{
                              remove: () => {
                                setSelectedZoneId(null);
                                setSelectedLatLng(null);
                              },
                            }}
                          >
                            <div className="pickup-point-details-zone-popup">
                              <div className="pickup-point-details-zone-popup__text">
                                <div className="pickup-point-details-zone-popup__price">
                                  {selectedZone?.price ?? 0} ₽
                                </div>
                                {selectedZone?.deliveryMinutesFrom != null &&
                                  selectedZone.deliveryMinutesTo != null && (
                                    <div className="pickup-point-details-zone-popup__time">
                                      {selectedZone.deliveryMinutesFrom}–
                                      {selectedZone.deliveryMinutesTo} мин
                                    </div>
                                  )}
                              </div>
                              <div className="pickup-point-details-zone-popup__actions">
                                <button
                                  type="button"
                                  className="pickup-point-details-zone-popup__icon pickup-point-details-zone-popup__edit"
                                  aria-label="Редактировать зону"
                                  title="Редактировать зону"
                                  onClick={() => {
                                    if (!selectedZone) return;
                                    setEditingZoneId(selectedZone.id);
                                    setZonePrice(String(selectedZone.price));
                                    setZoneDeliveryFrom(
                                      selectedZone.deliveryMinutesFrom != null
                                        ? String(selectedZone.deliveryMinutesFrom)
                                        : '',
                                    );
                                    setZoneDeliveryTo(
                                      selectedZone.deliveryMinutesTo != null
                                        ? String(selectedZone.deliveryMinutesTo)
                                        : '',
                                    );
                                    setDraftPolygon(
                                      selectedZone.polygon.map((point) => ({
                                        lat: point.lat,
                                        lng: point.lng,
                                      })),
                                    );
                                    setIsDrawingZone(true);
                                    setSelectedZoneId(null);
                                    setSelectedLatLng(null);
                                  }}
                                >
                                  <IoCreateOutline size={15} />
                                </button>
                                <button
                                  type="button"
                                  className="pickup-point-details-zone-popup__icon pickup-point-details-zone-popup__delete"
                                  aria-label="Удалить зону"
                                  title="Удалить зону"
                                  onClick={() => {
                                    if (!selectedZoneId) return;
                                    setZoneIdPendingDelete(selectedZoneId);
                                  }}
                                >
                                  <IoTrashOutline size={15} />
                                </button>
                              </div>
                            </div>
                          </Popup>
                        )}
                      </MapContainer>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="schedule" pt="md" className="pickup-point-details-tab-panel">
            <div className="pickup-point-schedule-desktop pickup-point-schedule-table">
              <EditableDataTable
                title="Расписание работы"
                columns={workingHoursColumns}
                data={workingHoursRows}
                onSave={handleSaveWorkingHours}
                isLoading={updatePickupPoint.isPending}
                transformEditableUpdate={handleWorkingHoursEditableUpdate}
              />
            </div>

            <div className="pickup-point-schedule-mobile">
              <div className="pickup-point-schedule-mobile__head">
                <h3 className="pickup-point-schedule-mobile__title">Расписание работы</h3>
                <div className="pickup-point-schedule-mobile__actions">
                  {scheduleEditing ? (
                    <>
                      <QuietIconButton
                        icon={<IoCheckmark size={18} />}
                        tone="accent"
                        onClick={() => void saveScheduleDraft()}
                        loading={updatePickupPoint.isPending}
                        aria-label="Сохранить"
                      />
                      <QuietIconButton
                        icon={<IoClose size={18} />}
                        onClick={cancelScheduleEdit}
                        disabled={updatePickupPoint.isPending}
                        aria-label="Отменить"
                      />
                    </>
                  ) : (
                    <QuietIconButton
                      icon={<IoCreateOutline size={18} />}
                      onClick={startScheduleEdit}
                      disabled={updatePickupPoint.isPending}
                      aria-label="Редактировать"
                    />
                  )}
                </div>
              </div>
              <p className="pickup-point-schedule-mobile__lead">
                Изменения в понедельнике применяются ко всем дням, пока не поменяете их отдельно.
              </p>
              <div className="pickup-point-schedule-mobile__list">
                {(scheduleEditing && scheduleDraft ? scheduleDraft : workingHoursRows).map((row) => {
                  const dayMeta = DAYS_OF_WEEK.find((d) => d.key === row.id);
                  const dayLabel = dayMeta?.label ?? row.day;
                  const hasTimes = Boolean(row.from?.trim() && row.to?.trim());
                  return (
                    <div
                      key={row.id}
                      className={`pickup-point-schedule-card${scheduleEditing ? ' pickup-point-schedule-card--editing' : ''}${!row.enabled ? ' pickup-point-schedule-card--off' : ''}`}
                    >
                      {scheduleEditing ? (
                        <>
                          <div className="pickup-point-schedule-card__top">
                            <span className="pickup-point-schedule-card__day">{dayLabel}</span>
                            <Tooltip
                              label="Сначала укажите время «С» и «До»"
                              disabled={hasTimes}
                              withArrow
                              position="top"
                              openDelay={180}
                              multiline
                              w={220}
                            >
                              <span className="pickup-point-details-switch-wrap">
                                <Switch
                                  checked={row.enabled}
                                  disabled={!hasTimes || updatePickupPoint.isPending}
                                  onChange={(e) => {
                                    const next = e.currentTarget.checked;
                                    if (next && !hasTimes) return;
                                    updateScheduleDraftField(row.id, 'enabled', next);
                                  }}
                                  size="xs"
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <div className="pickup-point-schedule-card__times">
                            <div className="pickup-point-schedule-card__field">
                              <span className="pickup-point-schedule-card__label">С</span>
                              <ScheduleTimeInput
                                value={row.from}
                                onChange={(from) => updateScheduleDraftField(row.id, 'from', from)}
                                disabled={row.allDay || updatePickupPoint.isPending}
                              />
                            </div>
                            <div className="pickup-point-schedule-card__field">
                              <span className="pickup-point-schedule-card__label">До</span>
                              <ScheduleTimeInput
                                value={row.to}
                                onChange={(to) => updateScheduleDraftField(row.id, 'to', to)}
                                disabled={row.allDay || updatePickupPoint.isPending}
                              />
                            </div>
                          </div>
                          <label className="pickup-point-schedule-card__allday">
                            <Checkbox
                              checked={row.allDay}
                              disabled={updatePickupPoint.isPending}
                              onChange={(e) =>
                                updateScheduleDraftField(row.id, 'allDay', e.currentTarget.checked)
                              }
                              aria-label="Круглосуточно"
                            />
                            <span>Круглосуточно</span>
                          </label>
                        </>
                      ) : (
                        <>
                          <div className="pickup-point-schedule-card__top">
                            <span className="pickup-point-schedule-card__day">{dayLabel}</span>
                            <span
                              className={`pickup-point-schedule-card__badge${row.enabled ? ' pickup-point-schedule-card__badge--on' : ''}`}
                            >
                              {row.enabled ? 'Работает' : 'Выходной'}
                            </span>
                          </div>
                          <div className="pickup-point-schedule-card__meta">
                            {!row.enabled
                              ? '—'
                              : row.allDay
                                ? 'Круглосуточно'
                                : hasTimes
                                  ? `${row.from} – ${row.to}`
                                  : 'Время не указано'}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="exceptions" pt="md" className="pickup-point-details-tab-panel">
            <div className="pickup-point-exceptions-desktop">
              <EditableDataTable
                title="Исключения"
                description="Особые дни: выходные, сокращённый день или другие часы вместо обычного расписания."
                columns={exceptionsColumns}
                data={exceptionsRows}
                onSave={handleSaveExceptions}
                isLoading={exceptionsBusy}
                rowEditMode={true}
                getNewRow={getNewExceptionRow}
                emptyMessage="Нет исключений"
                addLabel="Добавить исключение"
              />
            </div>

            <div className="pickup-point-exceptions-mobile">
              <div className="pickup-point-exceptions-mobile__head">
                <h3 className="pickup-point-exceptions-mobile__title">Исключения</h3>
                <QuietAddTrigger
                  onClick={startExceptionCreate}
                  disabled={exceptionDraft != null || exceptionsBusy}
                  aria-label="Добавить исключение"
                >
                  Добавить исключение
                </QuietAddTrigger>
              </div>
              <p className="pickup-point-exceptions-mobile__lead">
                Особые дни: выходные, сокращённый день или другие часы вместо обычного расписания.
              </p>

              {exceptionDraft && String(exceptionDraft.id).startsWith('new-') ? (
                <div className="pickup-point-exception-card pickup-point-exception-card--editing">
                  <div className="pickup-point-exception-card__field">
                    <span className="pickup-point-exception-card__label">Дата</span>
                    <DateInput
                      value={exceptionDraft.date}
                      onChange={(value) =>
                        setExceptionDraft((prev) =>
                          prev ? { ...prev, date: value ? new Date(value) : null } : prev,
                        )
                      }
                      valueFormat="DD.MM.YYYY"
                      placeholder="Дата"
                      size="sm"
                      className="pickup-point-exception-card__control"
                      disabled={exceptionsBusy}
                    />
                  </div>
                  <div className="pickup-point-exception-card__times">
                    <div className="pickup-point-exception-card__field">
                      <span className="pickup-point-exception-card__label">С</span>
                      <Input
                        type="time"
                        value={exceptionDraft.from}
                        onChange={(e) => {
                          const from = e.currentTarget.value;
                          setExceptionDraft((prev) => (prev ? { ...prev, from } : prev));
                        }}
                        size="xs"
                        disabled={exceptionsBusy}
                      />
                    </div>
                    <div className="pickup-point-exception-card__field">
                      <span className="pickup-point-exception-card__label">До</span>
                      <Input
                        type="time"
                        value={exceptionDraft.to}
                        onChange={(e) => {
                          const to = e.currentTarget.value;
                          setExceptionDraft((prev) => (prev ? { ...prev, to } : prev));
                        }}
                        size="xs"
                        disabled={exceptionsBusy}
                      />
                    </div>
                  </div>
                  <div className="pickup-point-exception-card__field">
                    <span className="pickup-point-exception-card__label">Причина</span>
                    <Input
                      type="text"
                      value={exceptionDraft.reason}
                      onChange={(e) => {
                        const reason = e.currentTarget.value;
                        setExceptionDraft((prev) => (prev ? { ...prev, reason } : prev));
                      }}
                      placeholder="Например, праздник"
                      size="xs"
                      disabled={exceptionsBusy}
                    />
                  </div>
                  <div className="pickup-point-exception-card__row">
                    <span className="pickup-point-exception-card__label">Работает</span>
                    <Switch
                      checked={exceptionDraft.enabled}
                      onChange={(e) => {
                        const enabled = e.currentTarget.checked;
                        setExceptionDraft((prev) => (prev ? { ...prev, enabled } : prev));
                      }}
                      size="xs"
                      disabled={exceptionsBusy}
                    />
                  </div>
                  <div className="pickup-point-exception-card__actions">
                    <QuietIconButton
                      icon={<IoCheckmark size={18} />}
                      tone="accent"
                      onClick={() => void saveExceptionDraft()}
                      loading={exceptionsBusy}
                      aria-label="Сохранить"
                    />
                    <QuietIconButton
                      icon={<IoClose size={18} />}
                      onClick={cancelExceptionEdit}
                      disabled={exceptionsBusy}
                      aria-label="Отменить"
                    />
                  </div>
                </div>
              ) : null}

              {exceptionsRows.length === 0 &&
              !(exceptionDraft && String(exceptionDraft.id).startsWith('new-')) ? (
                <div className="pickup-point-exceptions-mobile__empty">Нет исключений</div>
              ) : (
                <div className="pickup-point-exceptions-mobile__list">
                  {exceptionsRows.map((row) => {
                    const isEditing = exceptionDraft?.id === row.id;
                    const draft = isEditing ? exceptionDraft : null;
                    return (
                      <div
                        key={row.id}
                        className={`pickup-point-exception-card${isEditing ? ' pickup-point-exception-card--editing' : ''}`}
                      >
                        {isEditing && draft ? (
                          <>
                            <div className="pickup-point-exception-card__field">
                              <span className="pickup-point-exception-card__label">Дата</span>
                              <DateInput
                                value={draft.date}
                                onChange={(value) =>
                                  setExceptionDraft((prev) =>
                                    prev ? { ...prev, date: value ? new Date(value) : null } : prev,
                                  )
                                }
                                valueFormat="DD.MM.YYYY"
                                placeholder="Дата"
                                size="sm"
                                className="pickup-point-exception-card__control"
                                disabled={exceptionsBusy}
                              />
                            </div>
                            <div className="pickup-point-exception-card__times">
                              <div className="pickup-point-exception-card__field">
                                <span className="pickup-point-exception-card__label">С</span>
                                <Input
                                  type="time"
                                  value={draft.from}
                                  onChange={(e) => {
                                    const from = e.currentTarget.value;
                                    setExceptionDraft((prev) => (prev ? { ...prev, from } : prev));
                                  }}
                                  size="xs"
                                  disabled={exceptionsBusy}
                                />
                              </div>
                              <div className="pickup-point-exception-card__field">
                                <span className="pickup-point-exception-card__label">До</span>
                                <Input
                                  type="time"
                                  value={draft.to}
                                  onChange={(e) => {
                                    const to = e.currentTarget.value;
                                    setExceptionDraft((prev) => (prev ? { ...prev, to } : prev));
                                  }}
                                  size="xs"
                                  disabled={exceptionsBusy}
                                />
                              </div>
                            </div>
                            <div className="pickup-point-exception-card__field">
                              <span className="pickup-point-exception-card__label">Причина</span>
                              <Input
                                type="text"
                                value={draft.reason}
                                onChange={(e) => {
                                  const reason = e.currentTarget.value;
                                  setExceptionDraft((prev) => (prev ? { ...prev, reason } : prev));
                                }}
                                size="xs"
                                disabled={exceptionsBusy}
                              />
                            </div>
                            <div className="pickup-point-exception-card__row">
                              <span className="pickup-point-exception-card__label">Работает</span>
                              <Switch
                                checked={draft.enabled}
                                onChange={(e) => {
                                  const enabled = e.currentTarget.checked;
                                  setExceptionDraft((prev) => (prev ? { ...prev, enabled } : prev));
                                }}
                                size="xs"
                                disabled={exceptionsBusy}
                              />
                            </div>
                            <div className="pickup-point-exception-card__actions">
                              <QuietIconButton
                                icon={<IoCheckmark size={18} />}
                                tone="accent"
                                onClick={() => void saveExceptionDraft()}
                                loading={exceptionsBusy}
                                aria-label="Сохранить"
                              />
                              <QuietIconButton
                                icon={<IoClose size={18} />}
                                onClick={cancelExceptionEdit}
                                disabled={exceptionsBusy}
                                aria-label="Отменить"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="pickup-point-exception-card__top">
                              <span className="pickup-point-exception-card__date">
                                {formatExceptionDate(row.date)}
                              </span>
                              <span
                                className={`pickup-point-exception-card__badge${row.enabled ? ' pickup-point-exception-card__badge--on' : ''}`}
                              >
                                {row.enabled ? 'Работает' : 'Выходной'}
                              </span>
                            </div>
                            <div className="pickup-point-exception-card__meta">
                              {(row.from || row.to)
                                ? `${row.from || '—'} – ${row.to || '—'}`
                                : 'Весь день'}
                            </div>
                            {row.reason ? (
                              <p className="pickup-point-exception-card__reason">{row.reason}</p>
                            ) : null}
                            <div className="pickup-point-exception-card__actions">
                              <QuietIconButton
                                icon={<IoCreateOutline size={18} />}
                                onClick={() => startExceptionEdit(row)}
                                disabled={exceptionDraft != null || exceptionsBusy}
                              >
                                Изменить
                              </QuietIconButton>
                              <QuietIconButton
                                icon={<IoTrashOutline size={18} />}
                                tone="danger"
                                onClick={() => void deleteExceptionRow(row.id)}
                                disabled={exceptionDraft != null || exceptionsBusy}
                              >
                                Удалить
                              </QuietIconButton>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="slots" pt="md" className="pickup-point-details-tab-panel">
            <Stack gap="md">
              <Select
                className="pickup-point-timezone"
                label="Часовой пояс точки"
                description="Расписание и слоты считаются в этом поясе. При смене адреса обычно определяется автоматически."
                data={TIMEZONE_OPTIONS}
                value={pickupPoint.timezone || 'Europe/Moscow'}
                onChange={(value) => {
                  if (typeof value === 'number') return;
                  void handleTimezoneChange(value);
                }}
                searchable
                disabled={updatePickupPoint.isPending}
              />
              <div className="pickup-point-slots-table">
                <EditableDataTable
                  title="Настройки слотов"
                  description="Эти параметры задают окна времени, которые покупатель видит при оформлении заказа на эту точку."
                  columns={slotsColumns}
                  data={slotsRows}
                  onSave={handleSaveSlots}
                  isLoading={updatePickupPoint.isPending}
                />
              </div>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="stocks" pt="md" className="pickup-point-details-tab-panel">
            <div className="pickup-point-details-section">
              <h3 className="pickup-point-details-section__title">Остатки по этой точке</h3>
              <p className="pickup-point-details-section__lead">
                Укажите количество товара на этой точке или включите «без ограничений». При заказах остатки будут уменьшаться.
              </p>
              {productStocksLoading ? (
                <div className="pickup-point-details-loader">
                  <Loader />
                </div>
              ) : productStockItems.length === 0 ? (
                <Text size="sm" c="dimmed">
                  В магазине нет товаров
                </Text>
              ) : (
                <>
                  <div className="pickup-point-stocks-desktop">
                    <div className="pickup-point-stocks-scroll">
                      <table className="pickup-point-stocks-table">
                        <thead>
                          <tr>
                            <th className="pickup-point-stocks-table__th pickup-point-stocks-table__th--img">Фото</th>
                            <th className="pickup-point-stocks-table__th pickup-point-stocks-table__th--name">Название</th>
                            <th className="pickup-point-stocks-table__th pickup-point-stocks-table__th--qty">Количество</th>
                            <th
                              className="pickup-point-stocks-table__th pickup-point-stocks-table__th--unlimited"
                              title="Без ограничений на этой точке"
                              aria-label="Без ограничений на этой точке"
                            >
                              <span className="pickup-point-stocks-table__infinity-head">∞</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productStockItems.map((row) => (
                            <PickupStockTableRow
                              key={row.productId}
                              row={row}
                              currentPickupPointId={pickupPointId}
                              pickupPoints={sellerPickupPoints}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="pickup-point-stocks-mobile">
                    {productStockItems.map((row) => (
                      <PickupStockMobileCard
                        key={row.productId}
                        row={row}
                        currentPickupPointId={pickupPointId}
                        pickupPoints={sellerPickupPoints}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </Tabs.Panel>
        </Tabs>
      </div>}
      <DoubleCheckModal
        isOpen={isDeletePickupModalOpen}
        onClose={() => setIsDeletePickupModalOpen(false)}
        title="Удалить точку самовывоза?"
        message={`Вы уверены, что хотите удалить точку самовывоза "${pickupPoint.address}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDeletePickup}
        isLoading={deletePickupPoint.isPending}
      />
      <DoubleCheckModal
        isOpen={zoneIdPendingDelete != null}
        onClose={() => setZoneIdPendingDelete(null)}
        title="Удалить зону доставки?"
        message={
          zonePendingDelete
            ? `Удалить зону со стоимостью ${zonePendingDelete.price ?? 0} ₽? Это действие нельзя отменить.`
            : 'Удалить выбранную зону доставки?'
        }
        secondButtonText="Удалить"
        secondButtonAction={confirmDeleteZone}
        isLoading={updatePickupPoint.isPending}
      />
      <Modal
        isOpen={showNewPickupHintsModal}
        onClose={() => setShowNewPickupHintsModal(false)}
        title="Следующие шаги"
        maxWidth={440}
      >
        <Stack gap="sm">
          <Stack
            gap="xs"
            component="ul"
            style={{ margin: 0, paddingLeft: '1.25rem', listStyleType: 'disc' }}
          >
            <Text size="sm" component="li">
              Укажите расписание работы магазина на вкладке «Расписание».
            </Text>
            {deliveryProvider === 'SELLER' ? (
              <Text size="sm" component="li">
                На вкладке «Доставка» добавьте хотя бы одну зону доставки.
              </Text>
            ) : (
              <Text size="sm" component="li">
                При необходимости настройте компенсацию доставки на вкладке «Доставка».
              </Text>
            )}
            <Text size="sm" component="li">
              Не забудьте указать остатки по товарам в каталоге.
            </Text>
          </Stack>
          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowNewPickupHintsModal(false);
                navigate('/products');
              }}
            >
              К товарам
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => setShowNewPickupHintsModal(false)}
            >
              Понятно
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
